import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Mail, 
  Calendar, 
  Download, 
  Eye, 
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Search,
  Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

const AdminDashboard = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterReason, setFilterReason] = useState('All');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/contacts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      } else {
        setError('Failed to fetch contact submissions');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.workEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterReason === 'All' || contact.reason === filterReason;
    
    return matchesSearch && matchesFilter;
  });

  const exportToCSV = () => {
    if (contacts.length === 0) return;

    const headers = ['First Name', 'Last Name', 'Work Email', 'Company', 'Phone', 'Country', 'Reason', 'Message', 'How Did You Hear', 'Privacy Consent', 'Submitted At'];
    const csvContent = [
      headers.join(','),
      ...contacts.map(contact => [
        contact.firstName || '',
        contact.lastName || '',
        contact.workEmail || '',
        contact.company || '',
        contact.phoneNumber || '',
        contact.country || '',
        contact.reason || '',
        `"${(contact.message || '').replace(/"/g, '""')}"`,
        contact.howDidYouHear || '',
        contact.privacyConsent ? 'Yes' : 'No',
        new Date(contact.submittedAt).toISOString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact_submissions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-white">Loading contact submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Card className="p-8 bg-red-500/10 border-red-500/20 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error Loading Data</h2>
          <p className="text-gray-300 mb-4">{error}</p>
          <Button onClick={fetchContacts} className="btn-primary">
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Contact Form Submissions
              </h1>
              <p className="text-gray-400">
                Total submissions: {contacts.length}
              </p>
            </div>
            <Button 
              onClick={exportToCSV}
              className="btn-primary flex items-center gap-2"
              disabled={contacts.length === 0}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </motion.div>

        {/* Filters */}
        <Card className="p-6 mb-6 bg-black/40 border-white/10">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={filterReason}
                onChange={(e) => setFilterReason(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                <option value="All">All Reasons</option>
                <option value="I'd like to schedule a demo">Demo Request</option>
                <option value="I'd like to learn more about Godquantum System products">Product Inquiry</option>
                <option value="I'm an existing Godquantum system Customer and need technical support">Technical Support</option>
                <option value="I'm interested in Careers at Godquantum Systems">Career Interest</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Statistics Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-emerald-500/10 border-emerald-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-300 text-sm font-medium">Total Submissions</p>
                <p className="text-2xl font-bold text-white">{contacts.length}</p>
              </div>
              <Users className="w-8 h-8 text-emerald-400" />
            </div>
          </Card>
          
          <Card className="p-6 bg-blue-500/10 border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-sm font-medium">This Week</p>
                <p className="text-2xl font-bold text-white">
                  {contacts.filter(c => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return new Date(c.submittedAt) > weekAgo;
                  }).length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-blue-400" />
            </div>
          </Card>
          
          <Card className="p-6 bg-purple-500/10 border-purple-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-300 text-sm font-medium">Demo Requests</p>
                <p className="text-2xl font-bold text-white">
                  {contacts.filter(c => c.reason?.includes('demo')).length}
                </p>
              </div>
              <Mail className="w-8 h-8 text-purple-400" />
            </div>
          </Card>
        </div>

        {/* Contacts Table */}
        <Card className="bg-black/40 border-white/10 relative overflow-hidden">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              Recent Submissions ({filteredContacts.length})
            </h3>
            
            {filteredContacts.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400">No submissions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Company</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Reason</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-gray-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((contact, index) => (
                      <motion.tr
                        key={contact.id || index}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="py-3 px-4 text-white">
                          {contact.firstName} {contact.lastName}
                        </td>
                        <td className="py-3 px-4 text-gray-300">{contact.workEmail}</td>
                        <td className="py-3 px-4 text-gray-300">{contact.company}</td>
                        <td className="py-3 px-4">
                          <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/20">
                            {contact.reason?.split(' ').slice(0, 3).join(' ')}...
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-300 text-sm">
                          {formatDate(contact.submittedAt)}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            onClick={() => setSelectedContact(contact)}
                            className="btn-secondary flex items-center gap-1"
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </Button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        {/* Contact Detail Modal */}
        {selectedContact && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-black border border-white/20 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h3 className="text-xl font-semibold text-white">Contact Details</h3>
                  <button
                    onClick={() => setSelectedContact(null)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">First Name</label>
                      <p className="text-white">{selectedContact.firstName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Last Name</label>
                      <p className="text-white">{selectedContact.lastName}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Work Email</label>
                    <p className="text-white">{selectedContact.workEmail}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Company</label>
                      <p className="text-white">{selectedContact.company}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
                      <p className="text-white">{selectedContact.phoneNumber || 'Not provided'}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
                    <p className="text-white">{selectedContact.country}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Reason for Contact</label>
                    <p className="text-white">{selectedContact.reason}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-3 max-h-32 overflow-y-auto">
                      <p className="text-white text-sm whitespace-pre-wrap">{selectedContact.message}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">How did you hear about us?</label>
                    <p className="text-white">{selectedContact.howDidYouHear}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    {selectedContact.privacyConsent ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <X className="w-4 h-4 text-red-400" />
                    )}
                    <span className="text-gray-300 text-sm">Privacy Policy Consent</span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Submitted At</label>
                    <p className="text-white">{formatDate(selectedContact.submittedAt)}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;